import React, { useEffect, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { adminMachineProductUseCase } from "../../../di/container";
import Table from "../../components/UI/Table";
import { Button } from "../../components/UI/Button";
import type { MachineProductEntity } from "../../../domain/types/machine-product";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import ConfirmMessageModal from "../../components/UI/ConfirmMessageModal";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";

const MACHINE_TYPE_LABELS: Record<string, string> = {
  PRINTER: "프린터",
  CUTTER: "커터",
  PLOTTER: "플로터",
};

const PROTOCOL_LABELS: Record<string, string> = {
  SNMP: "SNMP",
  FIERY: "Fiery",
  DAEHO: "대호",
  SHARED_FOLDER: "공유 폴더",
};

const MachineProductManagement: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<MachineProductEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmSubmit, setConfirmSubmit] = useState<() => void>(() => {});
  const { notifications, addItem: addNotification } = useNotifications();

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await adminMachineProductUseCase.getAll();
      setProducts(data);
    } catch (error) {
      console.error("장비 제품 목록 조회 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleDelete = (product: MachineProductEntity) => {
    setConfirmMessage(
      `"${product.machineName || product.machineDescription || product.id}" 장비 제품을 삭제할까요?`
    );
    setConfirmSubmit(() => {
      return () => {
        // 모달을 먼저 닫는다. 확인 버튼은 제출 함수만 부르고 스스로 닫지 않으므로,
        // 호출이 끝난 뒤에 닫으면 그 사이 다시 눌러 "이미 없음" 오류를 볼 수 있다.
        setConfirmMessage("");
        setConfirmSubmit(() => {});
        (async () => {
          try {
            await adminMachineProductUseCase.delete(product.id);
            addNotification("삭제되었습니다.");
            await loadData();
          } catch (error) {
            addNotification("삭제에 실패했습니다.");
          }
        })();
      };
    });
  };

  const filteredProducts = products.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.machineName?.toLowerCase().includes(q) ?? false) ||
      (item.machineDescription?.toLowerCase().includes(q) ?? false) ||
      item.manufacturer.toLowerCase().includes(q)
    );
  });

  const columnWidths = editMode
    ? ["60px", "110px", "200px", "250px", "70px", "50px"]
    : ["60px", "110px", "200px", "300px", "70px"];

  const head = editMode
    ? [<>유형</>, <>제조사</>, <>장비명</>, <>설명</>, <>프로토콜</>, <>삭제</>]
    : [<>유형</>, <>제조사</>, <>장비명</>, <>설명</>, <>프로토콜</>];

  return (
    <Pod24AdminLayout>
      {confirmMessage && (
        <ConfirmMessageModal
          message={confirmMessage}
          setMessage={setConfirmMessage}
          submitFunction={confirmSubmit}
        />
      )}
      <NotificationsList notifications={notifications} />

      <div className="mb-6">
        <h1
          className="text-[22px] font-bold text-[#222]"
          style={{ fontFamily: "Pretendard" }}
        >
          장비 OID 관리
        </h1>
        <p className="text-[13px] text-[#888] mt-1">
          라우터가 장비를 식별하는 데 사용하는 장비 제품 데이터베이스입니다.
        </p>
        <div className="flex items-center justify-between mt-3">
          <input
            type="text"
            className="border border-[#e0e0e0] rounded-[8px] px-3 py-1.5 text-[13px] w-[240px] focus:border-[#4285F4] focus:outline-none"
            placeholder="장비명, 설명, 제조사 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Button
            size="sm"
            variant={editMode ? "gray" : "outline"}
            onClick={() => setEditMode(!editMode)}
          >
            {editMode ? "편집 종료" : "편집"}
          </Button>
          <Button
            size="sm"
            onClick={() => navigate("/admin/machine-product/new")}
          >
            새 제품 등록
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-[#888] text-[14px]">
          불러오는 중...
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-10 text-[#888] text-[14px]">
          {searchQuery ? "검색 결과가 없습니다." : "등록된 장비 제품이 없습니다."}
        </div>
      ) : (
        <div className="text-[12px] overflow-x-auto">
          <Table
            columnWidths={columnWidths}
            isPaddingRightEnabled={false}
            onRowClick={(rowIndex) =>
              navigate(`/admin/machine-product/${filteredProducts[rowIndex].id}/edit`)
            }
            tabledata={{
              head,
              body: filteredProducts.map((item) => {
                const baseCols = [
                  <>
                    {MACHINE_TYPE_LABELS[item.machineType] || item.machineType}
                  </>,
                  <>{item.manufacturer}</>,
                  <>
                    <span className="block truncate">
                      {item.machineName || "-"}
                    </span>
                  </>,
                  <>
                    <span className="block truncate">
                      {item.machineDescription || "-"}
                    </span>
                  </>,
                  <>
                    {PROTOCOL_LABELS[item.protocol] || item.protocol}
                  </>,
                ];

                if (editMode) {
                  baseCols.push(
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="flex justify-center"
                    >
                      <button
                        className="p-1.5 rounded hover:bg-red-50 text-[#F24A4A] transition-colors"
                        onClick={() => handleDelete(item)}
                        title="삭제"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                }

                return baseCols;
              }),
            }}
          />
        </div>
      )}
    </Pod24AdminLayout>
  );
};

export default MachineProductManagement;